import React, { useState, useEffect, useRef } from 'react';
import { YandexMapSelector } from '../../ui/YandexMapSelector/YandexMapSelector';

const MapComponent = ({ deliveries, hoveredDelivery }) => {
    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const [markers, setMarkers] = useState([]);
    const [deliveriesByLocation, setDeliveriesByLocation] = useState({});
    const [courierRoutes, setCourierRoutes] = useState([]);
    const mapRef = useRef(null);

    // Hardcoded start point
    const START_POINT = { lat: 41.722520, lng: 44.780379 };

    // Helper function to safely render a value that might be an object
    const renderValue = (value) => {
        if (typeof value === 'object' && value !== null) {
            // If it's a translation object with language keys
            if (value.en || value.ge || value.ru) {
                return value.en || value.ge || value.ru || '';
            }
            // For other object types, convert to string
            return JSON.stringify(value);
        }
        return value;
    };

    useEffect(() => {
        if (deliveries && deliveries.length > 0) {
            // Group deliveries by coordinates
            const locationGroups = {};
            
            deliveries.forEach(delivery => {
                if (delivery.latitude && delivery.longitude) {
                    const key = `${delivery.latitude},${delivery.longitude}`;
                    if (!locationGroups[key]) {
                        locationGroups[key] = [];
                    }
                    locationGroups[key].push(delivery);
                }
            });
            
            setDeliveriesByLocation(locationGroups);

            // Process courier routes first
            const courierGroups = {};
            const courierColors = [
                '#FF0000', // Bright Red
                '#0000FF', // Bright Blue
                '#8B0000', // Dark Red
                '#800080', // Purple
                '#FF8000', // Orange
                '#FFFF00', // Yellow
                '#FF1493', // Deep Pink
                '#8B4513', // Saddle Brown
                '#000000', // Black
                '#00FFFF', // Cyan
                '#FF69B4', // Hot Pink
                '#32CD32', // Lime Green
                '#DC143C', // Crimson
                '#4B0082', // Indigo
                '#FFD700'  // Gold
            ];
            
            // Group deliveries by courier
            deliveries.forEach(delivery => {
                if (delivery.assignedCourier && delivery.latitude && delivery.longitude) {
                    if (!courierGroups[delivery.assignedCourier]) {
                        courierGroups[delivery.assignedCourier] = [];
                    }
                    courierGroups[delivery.assignedCourier].push({
                        lat: delivery.latitude,
                        lng: delivery.longitude,
                        deliveryInterval: delivery.deliveryInterval || '',
                        clientName: delivery.clientName,
                        customOrder: delivery.customOrder,
                        deliveryId: delivery.id
                    });
                }
            });
            
            // Create markers with count for locations with multiple deliveries
            const validMarkers = Object.entries(locationGroups).map(([key, locationDeliveries]) => {
                const [lat, lng] = key.split(',').map(Number);
                const delivery = locationDeliveries[0]; // Use the first delivery for marker details
                
                return {
                    id: key,
                    lat: lat,
                    lng: lng,
                    title: delivery.clientName,
                    address: `${delivery.deliveryStreet}, ${delivery.buidlingNumber}${delivery.apartmnetNumber ? `, apt. ${delivery.apartmnetNumber}` : ''}`,
                    delivery: delivery,
                    // Set these to null to prevent the default tooltips from showing
                    balloonContent: null,
                    hintContent: null,
                    // Add count for locations with multiple deliveries
                    count: locationDeliveries.length > 1 ? locationDeliveries.length : null,
                    // Store all deliveries for this location
                    allDeliveries: locationDeliveries,
                    // Add courier information
                    courier: delivery.assignedCourier || null
                };
            });

            // Add start point marker if there are any courier routes
            if (Object.keys(courierGroups).length > 0) {
                const startMarker = {
                    id: 'start-point',
                    lat: START_POINT.lat,
                    lng: START_POINT.lng,
                    title: 'Start Point',
                    address: 'Delivery Start Location',
                    delivery: null,
                    balloonContent: 'Courier Start Point',
                    hintContent: 'All routes start here',
                    count: null,
                    allDeliveries: [],
                    courier: 'start'
                };
                validMarkers.push(startMarker);
            }

            setMarkers(validMarkers);

            // Create routes for each courier
            const routes = Object.entries(courierGroups).map(([courier, deliveryPoints], index) => {
                // Helper function to calculate distance between two points using Haversine formula
                const calculateDistance = (lat1, lng1, lat2, lng2) => {
                    const R = 6371; // Earth's radius in kilometers
                    const dLat = (lat2 - lat1) * Math.PI / 180;
                    const dLng = (lng2 - lng1) * Math.PI / 180;
                    const a = 
                        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                        Math.sin(dLng / 2) * Math.sin(dLng / 2);
                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                    return R * c; // Distance in kilometers
                };

                // Sort delivery points respecting custom order from drag and drop
                const sortedPoints = deliveryPoints.sort((a, b) => {
                    // First check if both have custom order (from drag and drop reordering)
                    if (a.customOrder !== undefined && b.customOrder !== undefined) {
                        return a.customOrder - b.customOrder;
                    }
                    
                    // If only one has custom order, prioritize it based on the order value
                    if (a.customOrder !== undefined && b.customOrder === undefined) {
                        return -1; // a comes first
                    }
                    if (a.customOrder === undefined && b.customOrder !== undefined) {
                        return 1; // b comes first
                    }
                    
                    // Default sorting: by delivery interval first, then by distance
                    const aInterval = a.deliveryInterval || '';
                    const bInterval = b.deliveryInterval || '';
                    const intervalCompare = aInterval.localeCompare(bInterval);
                    if (intervalCompare !== 0) return intervalCompare;
                    
                    // Then sort by distance from depot for same interval
                    if (a.lat && a.lng && b.lat && b.lng) {
                        const depotLat = 41.722520;
                        const depotLng = 44.780379;
                        
                        const aDistanceFromDepot = calculateDistance(depotLat, depotLng, a.lat, a.lng);
                        const bDistanceFromDepot = calculateDistance(depotLat, depotLng, b.lat, b.lng);
                        
                        return aDistanceFromDepot - bDistanceFromDepot;
                    }
                    
                    return 0;
                });

                // Debug logging to see route order
                console.log(`Route for ${courier}:`, sortedPoints.map(p => ({
                    id: p.deliveryId,
                    customOrder: p.customOrder,
                    interval: p.deliveryInterval,
                    client: p.clientName
                })));

                // Create route: start point -> delivery points
                const routePoints = [
                    START_POINT, // Start from hardcoded point
                    ...sortedPoints.map(point => ({ lat: point.lat, lng: point.lng }))
                ];

                return {
                    courier,
                    points: routePoints,
                    color: courierColors[index % courierColors.length],
                    deliveryCount: sortedPoints.length
                };
            });

            setCourierRoutes(routes);
            
            console.log('Created markers:', validMarkers);
            console.log('Created courier routes:', routes);
            
            // Reset selected delivery when deliveries change
            setSelectedDelivery(null);
        } else {
            // If no deliveries, clear markers and routes
            setMarkers([]);
            setDeliveriesByLocation({});
            setCourierRoutes([]);
            setSelectedDelivery(null);
        }
    }, [deliveries]);

    // Force map resize when deliveries are loaded to ensure it fills container properly
    useEffect(() => {
        if (deliveries && deliveries.length > 0) {
            // Small delay to ensure DOM has updated
            const timer = setTimeout(() => {
                // Dispatch a resize event to trigger map recalculation
                window.dispatchEvent(new Event('resize'));
            }, 150);
            
            return () => clearTimeout(timer);
        }
    }, [deliveries]);

    const handleMarkerClick = (marker) => {
        // If there are multiple deliveries at this location, show the first one
        // In a real app, you might want to show a list to select from
        setSelectedDelivery(marker.delivery);
    };

    const handleCloseInfo = () => {
        setSelectedDelivery(null);
    };

    const getCenterCoordinates = () => {
        if (markers.length > 0) {
            return {
                initialLat: markers[0].lat,
                initialLng: markers[0].lng
            };
        }
        // Default to Kiev coordinates if no valid markers
        return {
            initialLat: 50.4501,
            initialLng: 30.5234
        };
    };

    const center = getCenterCoordinates();

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }} ref={mapRef}>
            <YandexMapSelector
                markers={markers}
                onMarkerClick={handleMarkerClick}
                initialLat={center.initialLat}
                initialLng={center.initialLng}
                readOnly={true}
                disableTooltips={true}
                showCount={true}
                hoveredDelivery={hoveredDelivery}
                courierRoutes={courierRoutes}
                key={`map-${deliveries?.length || 0}-${JSON.stringify(courierRoutes.map(r => r.points))}`} // Force re-render when deliveries or routes change
            />
            {selectedDelivery && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'white',
                    padding: '15px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    maxWidth: '300px',
                    zIndex: 1000
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', wordBreak: 'break-word' }}>{renderValue(selectedDelivery.clientName)}</h3>
                        <button 
                            onClick={handleCloseInfo}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '18px',
                                cursor: 'pointer',
                                color: '#666',
                                marginLeft: '10px'
                            }}
                        >
                            ×
                        </button>
                    </div>
                    <div style={{ fontSize: '14px' }}>
                        <div style={{ marginBottom: '8px' }}>
                            <strong>Address:</strong> {renderValue(selectedDelivery.deliveryStreet)}, {renderValue(selectedDelivery.buidlingNumber)}
                            {selectedDelivery.apartmnetNumber && <span>, apt. {renderValue(selectedDelivery.apartmnetNumber)}</span>}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                            {selectedDelivery.entranceNumber && (
                                <div><strong>Entrance:</strong> {renderValue(selectedDelivery.entranceNumber)}</div>
                            )}
                            {selectedDelivery.floorNumber && (
                                <div><strong>Floor:</strong> {renderValue(selectedDelivery.floorNumber)}</div>
                            )}
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <strong>Contact:</strong> {renderValue(selectedDelivery.contact)}
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <strong>Delivery Interval:</strong> {renderValue(selectedDelivery.deliveryInterval)}
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <strong>Diet:</strong> {renderValue(selectedDelivery.dietName)}
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <strong>Courier:</strong> {selectedDelivery.assignedCourier ? (
                                <span style={{ color: '#1E88E5' }}>{renderValue(selectedDelivery.assignedCourier)}</span>
                            ) : (
                                <span style={{ color: '#F44336' }}>Not assigned</span>
                            )}
                        </div>
                        <div>
                            <strong>Coordinates:</strong> {renderValue(selectedDelivery.latitude)}, {renderValue(selectedDelivery.longitude)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapComponent; 